import { createContext, RefObject, useContext } from 'react';

import { Bean } from '@dreryk/react-common';

import { FormsTemplateListItem } from './FormsTemplates.types';
import { FormsTemplatesApi, VisitFormsTemplateMode } from './state/FormsTemplatesState.hooks';

type WithReadOnlyMode<ITEM extends Bean> = {
   mode: VisitFormsTemplateMode;
   activeItem: FormsTemplateListItem<ITEM>;
};
type WithoutReadOnlyMode = {
   mode: undefined;
   activeItem: undefined;
};

export type FormsTemplatesContext<ITEM extends Bean> = {
   setEditItem: (item: ITEM) => void;
   setReadOnlyItem: (item: ITEM | undefined) => void;
   disableSwitching: VoidFunction;
   enableSwitching: VoidFunction;
   addNewItem: VoidFunction;
   removeItem: (itemId: number | string) => void;
   activeItemName: string | undefined;
   switchingDisabled: boolean;
   items: ITEM[];
   templatesApiRef: RefObject<FormsTemplatesApi>;
   setActiveItemName: (itemName: string) => void;
} & (WithReadOnlyMode<ITEM> | WithoutReadOnlyMode);

const formsTemplatesContext = createContext<FormsTemplatesContext<Bean> | null>(null);

export const useFormsTemplatesContext = <ITEM extends Bean>(): FormsTemplatesContext<ITEM> => {
   const context = useContext(formsTemplatesContext);

   if (!context) {
      throw new Error('useFormsTemplatesContext must be used within FormsTemplatesContextProvider');
   }

   return context as FormsTemplatesContext<ITEM>;
};

type FormsTemplatesContextProviderProps<ITEM extends Bean> = {
   readonly children: JSX.Element;
   readonly value: FormsTemplatesContext<ITEM>;
};

const { Provider } = formsTemplatesContext;

export const FormsTemplatesContextProvider = <ITEM extends Bean>(
   props: FormsTemplatesContextProviderProps<ITEM>
): JSX.Element => {
   return <Provider value={props.value as FormsTemplatesContext<Bean>}>{props.children}</Provider>;
};
